import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import SearchOverlay from "@/components/feature/SearchOverlay";

interface NavChild {
  label: string;
  to: string;
}

interface NavItem {
  label: string;
  to: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  { label: "首页", to: "/" },
  {
    label: "品牌动态",
    to: "/news",
    children: [
      { label: "最新发布", to: "/news" },
      { label: "联名合作", to: "/news?tag=联名合作" },
      { label: "门店活动", to: "/news?tag=活动" },
      { label: "品牌荣誉", to: "/news?tag=荣誉" },
    ],
  },
  {
    label: "生活方式",
    to: "/lifestyle",
    children: [
      { label: "美食", to: "/lifestyle?tag=美食" },
      { label: "旅行", to: "/lifestyle?tag=旅行" },
      { label: "健康", to: "/lifestyle?tag=健康" },
      { label: "居家", to: "/lifestyle?tag=居家" },
    ],
  },
  {
    label: "潮流趋势",
    to: "/trend",
    children: [
      { label: "色彩", to: "/trend?tag=色彩" },
      { label: "材质", to: "/trend?tag=材质" },
      { label: "风格", to: "/trend?tag=风格" },
      { label: "配饰", to: "/trend?tag=配饰" },
    ],
  },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenAccordion(null);
  }, [location.pathname, location.search]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !mobileOpen;
  const brandClass = transparent ? "text-background-50" : "text-foreground-950";
  const linkClass = transparent
    ? "text-background-50/85 hover:text-background-50"
    : "text-foreground-700 hover:text-foreground-950";

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          transparent
            ? "bg-transparent"
            : "border-b border-background-200 bg-background-50/95 backdrop-blur"
        }`}
      >
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-8 lg:px-12">
          <Link
            to="/"
            className="flex cursor-pointer items-center gap-2.5 whitespace-nowrap"
            aria-label="台北娜娜 首页"
          >
            {isHome ? (
              <h1 className={`font-heading text-xl font-extrabold tracking-wide md:text-2xl ${brandClass}`}>
                台北娜娜
              </h1>
            ) : (
              <span className={`font-heading text-xl font-extrabold tracking-wide md:text-2xl ${brandClass}`}>
                台北娜娜
              </span>
            )}
            <span
              className={`hidden text-[10px] font-medium uppercase tracking-[0.28em] sm:block ${
                transparent ? "text-background-50/60" : "text-foreground-400"
              }`}
            >
              Taipei Nana
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <div key={item.label} className="group relative">
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${linkClass} ${
                      isActive && !transparent ? "text-foreground-950" : ""
                    }`
                  }
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-sm opacity-70" />}
                </NavLink>
                {item.children && (
                  <div className="invisible absolute left-0 top-full w-44 translate-y-2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50 py-2 shadow-lg">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.to}
                          className="block cursor-pointer whitespace-nowrap px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="搜索"
              className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors ${
                transparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <Link
              to="/contact"
              className="hidden whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 md:inline-flex"
            >
              订阅资讯
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="菜单"
              className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors lg:hidden ${
                transparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-6 pt-2 lg:hidden">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-background-100 last:border-none">
                <div className="flex items-center justify-between">
                  <Link
                    to={item.to}
                    className="flex-1 cursor-pointer py-3.5 text-sm font-medium text-foreground-900"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      type="button"
                      aria-label={`展开${item.label}`}
                      onClick={() =>
                        setOpenAccordion((prev) => (prev === item.label ? null : item.label))
                      }
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
                    >
                      <i
                        className={
                          openAccordion === item.label
                            ? "ri-subtract-line"
                            : "ri-add-line"
                        }
                      />
                    </button>
                  )}
                </div>
                {item.children && openAccordion === item.label && (
                  <div className="pb-3 pl-3">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.to}
                        className="block cursor-pointer py-2 text-sm text-foreground-600"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              className="mt-4 flex items-center justify-center whitespace-nowrap rounded-full bg-accent-500 px-5 py-3 text-sm font-medium text-background-50"
            >
              订阅资讯
            </Link>
          </div>
        )}
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}